import * as React from "react";
import { createContext, ReactNode, useCallback, useContext, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { ActionMenuItem } from "../action-menu/ActionMenuItem";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type SummaryListingStateType = {
    deviceCount: number;
    wellCount: number;
    tankBatteryCount: number;
    alarmLynxCount: number;
    compressorLynxCount: number;
    viewOptionState: ViewOptionStateEnum;
    actions: ActionMenuItem[],
    viewMaximized: boolean;
};

enum SummaryListingActionType {
    DeviceCountChanged = "DEVICE_COUNT_CHANGED",
    WellCountChanged = "WELL_COUNT_CHANGED",
    TankBatteryCountChanged = "TANK_BATTERY_COUNT_CHANGED",
    AlarmLynxCountChanged = "ALARMLYNX_COUNT_CHANGED",
    CompressorLynxCountChanged = "COMPRESSORLYNX_COUNT_CHANGED",
    ViewOptionStateChanged = "VIEW_OPTION_STATE_CHANGED",
    ClearCountsChanged = 'CLEAR_COUNTS_CHANGED',
    ActionsChanged = 'SUMMARY_LISTING_ACTIONS_CHANGED',
    ViewMaximizedChanged = 'MAXIMIZE_CHANGED',
}

type SummaryListingAction = {
    type: SummaryListingActionType.DeviceCountChanged,
    payload: number;
} | {
    type: SummaryListingActionType.WellCountChanged,
    payload: number;
} | {
    type: SummaryListingActionType.TankBatteryCountChanged,
    payload: number;
} | {
    type: SummaryListingActionType.AlarmLynxCountChanged,
    payload: number;
} | {
    type: SummaryListingActionType.CompressorLynxCountChanged,
    payload: number;
} | {
    type: SummaryListingActionType.ClearCountsChanged,
    payload: void;
} | {
    type: SummaryListingActionType.ActionsChanged,
    payload: ActionMenuItem[];
} | {
    type: SummaryListingActionType.ViewOptionStateChanged,
    payload: ViewOptionStateEnum;
} | {
    type: SummaryListingActionType.ViewMaximizedChanged;
    payload: boolean;

};

const initialState: SummaryListingStateType = {
    deviceCount: 0,
    wellCount: 0,
    tankBatteryCount: 0,
    alarmLynxCount: 0,
    compressorLynxCount: 0,
    viewOptionState: ViewOptionStateEnum.Default,
    actions: [],
    viewMaximized: false
};

function summaryListingReducer(state: SummaryListingStateType, action: SummaryListingAction): SummaryListingStateType {
    switch (action.type) {
        case SummaryListingActionType.DeviceCountChanged:
            return {
                ...state,
                deviceCount: action.payload
            };
        case SummaryListingActionType.WellCountChanged:
            return {
                ...state,
                wellCount: action.payload
            };
        case SummaryListingActionType.TankBatteryCountChanged:
            return {
                ...state,
                tankBatteryCount: action.payload
            };
        case SummaryListingActionType.AlarmLynxCountChanged:
            return {
                ...state,
                alarmLynxCount: action.payload
            };
        case SummaryListingActionType.CompressorLynxCountChanged:
            return {
                ...state,
                compressorLynxCount: action.payload
            };
        case SummaryListingActionType.ViewOptionStateChanged:
            return {
                ...state,
                viewOptionState: action.payload
            };
        case SummaryListingActionType.ClearCountsChanged:
            return {...state,
            deviceCount: 0,
            wellCount: 0,
            tankBatteryCount: 0,
            alarmLynxCount: 0,
            compressorLynxCount: 0
        };

        case SummaryListingActionType.ActionsChanged:
            return {...state,
            actions: action.payload
        };

        case SummaryListingActionType.ViewMaximizedChanged:
            return {...state,
                viewMaximized: action.payload
            };

        default:
            return state;
    }
}

type SetCountFunctionType = (count: number) => void;
type setViewOptionStateFunctionType = (state: ViewOptionStateEnum) => void;
type clearCountsFunctionType = () => void;
type UseSummaryListingStateType = {
    state: SummaryListingStateType;
    setDeviceCount: SetCountFunctionType;
    setWellCount: SetCountFunctionType;
    setTankBatteryCount: SetCountFunctionType;
    setAlarmLynxCount: SetCountFunctionType;
    setCompressorLynxCount: SetCountFunctionType;
    setViewOptionState: setViewOptionStateFunctionType;
    clearCounts: clearCountsFunctionType;
    setActions: (value: ActionMenuItem[]) => void;
    setViewMaximized: (value: boolean) => void;
};

function useSummaryListingState(): UseSummaryListingStateType {

    const [state, dispatch] = useReducer(summaryListingReducer, initialState);

    const setDeviceCount = useCallback((count: number) => dispatch({
            type: SummaryListingActionType.DeviceCountChanged,
            payload: count
        }), []);

    const setWellCount = useCallback((count: number) => dispatch({
            type: SummaryListingActionType.WellCountChanged,
            payload: count
        }), []);

    const setTankBatteryCount = useCallback((count: number) => dispatch({
            type: SummaryListingActionType.TankBatteryCountChanged,
            payload: count
        }), []);

    const setAlarmLynxCount = useCallback((count: number) => dispatch({
            type: SummaryListingActionType.AlarmLynxCountChanged,
            payload: count
        }), []);

    const setCompressorLynxCount = useCallback((count: number) => dispatch({
            type: SummaryListingActionType.CompressorLynxCountChanged,
            payload: count
        }), []);

    const setViewOptionState = useCallback((count: number) => dispatch({
            type: SummaryListingActionType.ViewOptionStateChanged,
            payload: count
        }), []);

    const clearCounts = useCallback(() => dispatch({
        type: SummaryListingActionType.ClearCountsChanged,
        payload: undefined
    }), []);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: SummaryListingActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: SummaryListingActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    const setViewMaximized = useCallback((value: boolean) => dispatch({
        type: SummaryListingActionType.ViewMaximizedChanged,
        payload: value
    }), []);


    return {
        state,
        setDeviceCount,
        setWellCount,
        setTankBatteryCount,
        setAlarmLynxCount,
        setCompressorLynxCount,
        setViewOptionState,
        clearCounts,
        setActions,
        setViewMaximized
    };
}

const SummaryListingContext = createContext<UseSummaryListingStateType | undefined>(undefined);

export type SummaryListingContextProviderProps = {
    children: ReactNode;
};

export default function SummaryListingContextProvider({ children }: SummaryListingContextProviderProps) {
    const summaryListingState = useSummaryListingState();

    return (
        <SummaryListingContext.Provider value={summaryListingState}>
            {children}
        </SummaryListingContext.Provider>
    );
}

export function useSummaryListingContext() {
    const context = useContext<UseSummaryListingStateType | undefined>(SummaryListingContext);
    if (!context) {
        throw new Error("useSummaryListingContext must be used within a SummaryListingContextProvider");
    }
    return context;
}